import {
  getPlatformVideoContentType,
  getPlatformVideoContentTypeDesc
} from '@/biz/platform';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { normalizePriceDollar } from '@/utils/normalizePrice';
import { Radio } from 'aha-components';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const isSamePriceField = (a: any, b: any) =>
  a?.contentType === b?.contentType && a?.actualGet === b?.actualGet;

function getAhaPicksListKey(orderId: string | undefined, list: any[]) {
  return `${orderId ?? ''}:${list.map((item) => item.contentType).join('|')}`;
}

export function toAhaPicksSelectedPriceField(field: any) {
  if (!field) return field;
  return {
    ...field,
    amount: field.amount ?? field.dealPrice,
    talentManagerCost: field.talentManagerCost ?? field.serviceFee
  };
}

export function getAhaPicksOfferList(order: any) {
  const history = order?.ahaPicksOffer?.history?.at(-1);
  return history
    ? (Object.values(history) as any[]).map(toAhaPicksSelectedPriceField)
    : [];
}

export function resolveAhaPicksOfferItem(order: any, contentType?: string) {
  const list = getAhaPicksOfferList(order);
  if (list.length === 0) return null;
  if (contentType) {
    return list.find((item) => item.contentType === contentType) ?? list[0];
  }
  return list[0];
}

export function prepareAhaPicksAcceptOffer(order: any) {
  const selected = useMultiplePricesStore.getState().selectedPriceFields?.[0];
  const offerItem = resolveAhaPicksOfferItem(order, selected?.contentType);
  if (offerItem) {
    useMultiplePricesStore.setState({ selectedPriceFields: [offerItem] });
  }
  return offerItem;
}

export function resolveOfferPriceValue(field: any) {
  const price = field?.actualGet;
  if (price == null || price === '') return null;
  const numeric = Number(price);
  if (Number.isNaN(numeric) || numeric <= 0) return null;
  return Math.round(numeric);
}

export function resolveAcceptOfferDisplayPrice(field: any) {
  const price = field?.actualGet;
  if (price == null || price === '') return null;
  const numeric = Number(price);
  if (Number.isNaN(numeric) || numeric <= 0) return null;
  return Math.round(numeric);
}

function formatDeductionAmount(amount?: number | string | null) {
  const value = Number(amount ?? 0);
  if (value > 0) {
    return `-${normalizePriceDollar(amount as number | string)}`;
  }
  if (value === 0) {
    return '$0';
  }
  return normalizePriceDollar(amount as number | string);
}

export default function AhaPicksPrePriceFields({
  order,
  className,
  getSetPriceRange
}: {
  order: any;
  className?: string;
  getSetPriceRange?: (selectedData?: any) => void;
}) {
  const { t } = useTranslation();
  const {
    selectedPriceFields,
    setSelectedPriceFields,
    ahaPicksSelectionInitializedKey
  } = useMultiplePricesStore();

  const list = useMemo(
    () => getAhaPicksOfferList(order),
    [order?.ahaPicksOffer?.history]
  );

  const listKey = getAhaPicksListKey(order?.id, list);

  // 每个 order + offer 列表只初始化一次（store 级别，避免 remount / 多实例重复覆盖）
  useEffect(() => {
    if (list.length === 0) return;
    if (ahaPicksSelectionInitializedKey === listKey) return;

    const selected = useMultiplePricesStore.getState().selectedPriceFields?.[0];
    let effectiveSelected: any;

    if (list.length === 1) {
      effectiveSelected = list[0];
      if (!isSamePriceField(selected, list[0])) {
        setSelectedPriceFields([list[0]]);
      }
    } else {
      const matchedItem = list.find(
        (item) => item.contentType === selected?.contentType
      );
      if (matchedItem) {
        effectiveSelected = matchedItem;
        if (!isSamePriceField(selected, matchedItem)) {
          setSelectedPriceFields([matchedItem]);
        }
      }
    }

    useMultiplePricesStore.setState({
      ahaPicksSelectionInitializedKey: listKey
    });

    if (effectiveSelected) {
      getSetPriceRange?.([effectiveSelected]);
    }
  }, [
    list,
    listKey,
    ahaPicksSelectionInitializedKey,
    setSelectedPriceFields,
    getSetPriceRange
  ]);

  if (list.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-col gap-2 w-full ${className}`}>
      {list.map((item: any) => {
        return (
          <div
            key={item.contentType}
            className={`bg-[#FAFAF9] rounded-[12px] p-3 flex flex-col gap-4 cursor-pointer ${selectedPriceFields?.find((el) => el?.contentType === item?.contentType) ? 'outline outline-1 outline-[#FF7728]' : ''}`}
            onClick={() => {
              setSelectedPriceFields([item]);
              getSetPriceRange?.([item]);
            }}
          >
            <div className="flex items-start gap-3">
              {list.length > 1 && (
                <div className="pt-1">
                  <Radio
                    checked={Boolean(
                      selectedPriceFields?.find(
                        (el) => el?.contentType === item?.contentType
                      )
                    )}
                  />
                </div>
              )}
              <div className="self-stretch inline-flex flex-col justify-center items-start">
                <div className="justify-start text-[#0D0D0D] text-base font-semibold font-['DM_Sans'] leading-6">
                  {item.contentType}
                </div>
                <div className="justify-start text-[#737373] text-xs font-normal font-['DM_Sans'] leading-4">
                  {getPlatformVideoContentTypeDesc(
                    getPlatformVideoContentType({
                      platform: order?.platform,
                      contentType: item?.contentType
                    }),
                    order?.platform
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <div className="justify-start text-[#525252] text-xs font-normal font-['DM_Sans'] leading-4">
                  {t('timeline.content_deal_price', {
                    defaultValue: 'Deal price'
                  })}
                </div>
                <div className="justify-start text-[#0D0D0D] text-sm font-medium font-['DM_Sans'] leading-5">
                  {normalizePriceDollar(item?.dealPrice)}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="justify-start text-[#525252] text-xs font-normal font-['DM_Sans'] leading-4">
                  {t('timeline.content_platform_service_fee', {
                    defaultValue: 'Service fee'
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <div className="justify-start text-[#0D0D0D] text-sm font-medium font-['DM_Sans'] leading-5">
                    {formatDeductionAmount(item?.serviceFee)}
                  </div>
                  <div className="justify-start text-[#A3A3A3] text-sm font-normal font-['Inter'] line-through leading-5">
                    {formatDeductionAmount(item?.subsidyAmount)}
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-[#D4D4D4] border-dashed"></div>
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="justify-start text-[#525252] text-xs font-normal font-['DM_Sans'] leading-4">
                {t('timeline.content_you_will_receive', {
                  defaultValue: 'You’ll receive'
                })}
              </div>
              <div className="justify-start text-[#0D0D0D] text-base font-semibold font-['DM_Sans'] leading-6">
                {normalizePriceDollar(item?.actualGet)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
